import React from 'react';
import Icon from '@/components/ui/AppIcon';

const ACTIVITIES = [
  {
    id: 'activity-001',
    title: 'Climate Vulnerability Mapping',
    type: 'Individual',
    time: '25 min',
    description:
      'Students receive a blank world map and a dataset of Climate Vulnerability Index scores. They color-code countries by vulnerability level and annotate with 3 key observations.',
    skills: ['Data analysis', 'Cartography', 'Critical thinking'],
    materials: ['Blank world map template', 'CVIndex dataset handout', 'Colored pencils'],
  },
  {
    id: 'activity-002',
    title: 'Stakeholder Role-Play: The Climate Summit',
    type: 'Group',
    time: '40 min',
    description:
      'Student groups represent different stakeholders at a mock climate summit: a Pacific Island nation, a major oil-producing country, an environmental NGO, and a multinational corporation. Each group prepares a 2-minute position statement and negotiates a shared agreement.',
    skills: ['Perspective-taking', 'Negotiation', 'Public speaking'],
    materials: ['Stakeholder role cards', 'Position statement template', 'Summit agenda'],
  },
  {
    id: 'activity-003',
    title: 'Personal Carbon Footprint + Global Connection',
    type: 'Individual',
    time: '20 min',
    description:
      'Students calculate their estimated annual carbon footprint using the provided calculator worksheet, then identify which of their top 3 emission sources most directly connects to climate impacts in low-income countries.',
    skills: ['Quantitative reasoning', 'Self-reflection', 'Systems thinking'],
    materials: ['Carbon footprint calculator worksheet', 'Emissions-impact connection chart'],
  },
  {
    id: 'activity-004',
    title: 'Policy Brief: One Climate Justice Proposal',
    type: 'Individual',
    time: '45 min',
    description:
      'Using the provided template, students write a 1-page policy brief proposing one concrete climate justice action. Must include: the problem, evidence of disproportionate impact, proposed solution, and expected outcome. Peer-reviewed before final submission.',
    skills: ['Persuasive writing', 'Evidence-based reasoning', 'Policy analysis'],
    materials: ['Policy brief template', 'Peer review rubric', 'Evidence sources list'],
  },
];

export default function ActivitiesTab() {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold text-stone-800 text-lg">Student Activities</h2>
        <span className="text-sm text-stone-500 bg-stone-100 px-3 py-1 rounded-full">
          4 activities · All materials included
        </span>
      </div>
      {ACTIVITIES?.map((activity) => (
        <div
          key={activity?.id}
          className="bg-white rounded-2xl border border-stone-200 p-6 hover:border-primary-200 hover:shadow-card transition-all duration-200"
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    activity?.type === 'Group' ?'bg-accent-50 text-accent-700' :'bg-primary-50 text-primary-700'
                  }`}
                >
                  {activity?.type}
                </span>
                <span className="text-xs text-stone-500 flex items-center gap-1">
                  <Icon name="ClockIcon" size={12} />
                  {activity?.time}
                </span>
              </div>
              <h3 className="font-semibold text-stone-800">{activity?.title}</h3>
            </div>
            <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-accent-700 bg-accent-50 hover:bg-accent-100 active:scale-95 transition-all duration-150 flex-shrink-0 border border-accent-200">
              <Icon name="ArrowDownTrayIcon" size={13} />
              Download
            </button>
          </div>

          <p className="text-sm text-stone-600 leading-relaxed mb-4">{activity?.description}</p>

          <div className="flex flex-wrap gap-3">
            <div>
              <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider block mb-1.5">
                Skills
              </span>
              <div className="flex flex-wrap gap-1.5">
                {activity?.skills?.map((skill) => (
                  <span
                    key={`skill-${activity?.id}-${skill}`}
                    className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full border border-purple-100"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
            <div className="ml-auto">
              <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider block mb-1.5">
                Materials
              </span>
              <div className="flex flex-wrap gap-1.5">
                {activity?.materials?.map((mat) => (
                  <span
                    key={`actmat-${activity?.id}-${mat}`}
                    className="text-xs px-2 py-0.5 bg-stone-100 text-stone-600 rounded-full"
                  >
                    {mat}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}